import { ChangeEvent, Fragment, ReactNode } from 'react'

import { TdProps, Tr, TrClickable, TrProps } from 'app/common/ui-components/table'
import { CommonRenderCell, defaultRenderCell } from './default-render-cell'

export type CommonRenderRowProps<
  T extends Record<string, unknown>,
  K extends keyof T & string = keyof T & string,
> = {
  rowData: T
  dataArrange: Array<K>
  rowIndex: number
  renderCell?: CommonRenderCell<T, K>
  onRowClick?: (rowData: T) => void
  cellProps?: Record<K, Partial<TdProps>>
  rowProps?: Partial<TrProps>
  settings?: Record<string, boolean>
  className?: string
}

export type CommonRenderRow<T extends Record<string, unknown>, K extends keyof T & string> = (
  props: CommonRenderRowProps<T, K>,
) => ReactNode

export const defaultRenderRow = <T extends Record<string, unknown>, K extends keyof T & string>({
  rowData,
  dataArrange,
  rowIndex,
  renderCell = defaultRenderCell,
  onRowClick,
  cellProps,
  rowProps,
  settings,
  className,
}: CommonRenderRowProps<T, K>) => {
  const content = dataArrange
    .filter((key) => (settings && settings[key] !== undefined ? settings[key] === true : true))
    .map((key, colIndex) => (
      <Fragment key={key}>
        {renderCell({
          columnKey: key,
          cellData: rowData[key],
          rowIndex,
          colIndex,
          rowData,
          cellProps: cellProps ? cellProps[key] : undefined,
        })}
      </Fragment>
    ))

  return onRowClick ? (
    <TrClickable
      className={className}
      alignTop
      interlineBorder
      {...rowProps}
      id={String(rowIndex)}
      onClick={() => onRowClick(rowData)}
    >
      {content}
    </TrClickable>
  ) : (
    <Tr className={className} alignTop interlineBorder {...rowProps}>
      {content}
    </Tr>
  )
}
